import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import type { CatchRow } from './catchRow';
import { relativeTime } from '../../lib/relativeTime';

interface CatchFeedProps {
  rows: CatchRow[];
  renderAction?: (row: CatchRow) => ReactNode;
  onRowClick?: (row: CatchRow) => void;
}

export function CatchFeed({ rows, renderAction, onRowClick }: CatchFeedProps) {
  const navigate = useNavigate();
  const handleClick = onRowClick ?? ((row: CatchRow) => navigate(`/catches/${row.id}`));

  return (
    <ul className="divide-y divide-slate-100 dark:divide-slate-700">
      {rows.map(row => (
        <li
          key={row.id}
          onClick={() => handleClick(row)}
          className="flex items-center justify-between gap-3 py-2.5 px-2 -mx-2 rounded-lg cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/40 transition-colors"
        >
          <div className="min-w-0 flex items-center gap-2 flex-wrap">
            <span className="font-medium text-slate-900 dark:text-slate-100">{row.fishTypeName}</span>
            {row.anglerName && (
              <>
                <span className="text-sm text-slate-400 dark:text-slate-500">by</span>
                <span className="text-sm text-slate-600 dark:text-slate-300">{row.anglerName}</span>
              </>
            )}
            {row.lengthInches != null && row.lengthInches > 0 && (
              <span className="rounded-full bg-teal-50 dark:bg-teal-900/20 px-2 py-0.5 text-xs font-medium text-teal-700 dark:text-teal-400">
                {row.lengthInches}&Prime;
              </span>
            )}
            {row.lureTypeName && (
              <span className="rounded-full bg-slate-100 dark:bg-slate-700 px-2 py-0.5 text-xs font-medium text-slate-600 dark:text-slate-300">
                {row.lureTypeName}
              </span>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <span className="text-xs text-slate-400 dark:text-slate-500">{relativeTime(row.caughtAt)}</span>
            {renderAction?.(row)}
          </div>
        </li>
      ))}
    </ul>
  );
}
